import React from 'react';
import { connect } from 'react-redux';
import { change, formValueSelector } from 'redux-form';
import AddCampaignForm from '../../../../Components/Forms/Client/Campaigns/AddCampaign';
import ContactsImportStats from '../../../../Components/ContactsImportStats';
import ModalsWrappedForm from '../../../../Components/Forms/ModalsWrappedForm';
import trans from '../../../../Translations/translation.service';
import { actions as allGroupsActions } from '../../../../Redux/Api/AllGroups';
import { actions as campaignsActions, types } from '../../../../Redux/Api/Campaigns';
import { actions as fileUploadActions } from '../../../../Redux/Ducks/FileUpload';
import { PRIVATE_PATHS } from '../../../config';
import { CAMPAIGNS_URL, FEMALE_CODE, JSON_HEADERS as headers } from '../../../../Constants';
import { ADD_ACTIONS, ADD_MODAL_ACTIONS, CAMPAIGN_FORM as form } from '../../../../Components/Forms/Client/Campaigns/config';
import { catchApiItemErrors } from '../../../../Helpers';
import Typography from '@material-ui/core/Typography';
import {
  extractImportStats,
  getScanStatus,
  isScanPending,
  pollCampaignScan,
} from '../../../../Helpers/campaignScan';

class AddCampaign extends React.Component {
  state = {
    createdId: null,
    importStats: null,
    scanStatus: null,
    scanError: null,
    scanning: false,
    // true cuando el scan async/sync terminó OK (el poll pisa reduxRestResponse)
    scanSucceeded: false,
  };

  createCampaign = ({ contacts_src_file, contacts, ...data }) => {
    const { afterSave } = this.props;
    const { createdId } = this.state;

    if (!data.file) {
      data.contacts = contacts;
    }

    if (!createdId) {
      return this.props.createCampaign(data)
        .then(async res => {
          const { id } = res.body.data;

          this.setState({ createdId: id });

          if (afterSave) {
            afterSave(id);
          }

          if (contacts_src_file) {
            await this.uploadContactsFile(id, contacts_src_file);
          }
        })
        .catch(
          catchApiItemErrors,
        );
    } else {
      return this.props.updateCampaign({ id: createdId, ...data })
        .then(async res => {
          if (contacts_src_file) {
            await this.uploadContactsFile(createdId, contacts_src_file);
          }
        })
        .catch(
          catchApiItemErrors,
        );
    }
  };

  getActions = () =>{
    const { onAcceptClick } = this.props;

    if (onAcceptClick) {
      const actions = [...ADD_MODAL_ACTIONS];
      actions[0].onClick = () => onAcceptClick();
      return actions;
    } else {
      return ADD_ACTIONS;
    }
  };

  getImportStats = () => {
    const { importStats, scanning, scanStatus, scanError } = this.state;
    const { file, response } = this.props;

    if (scanning || isScanPending(scanStatus)) {
      return (
        <Typography variant="body2">
          Procesando archivo de contactos…
        </Typography>
      );
    }

    if (scanError) {
      return (
        <Typography color="error" variant="body2">
          {scanError}
        </Typography>
      );
    }

    const stats = importStats
      || (file && response && response.data && extractImportStats(response.data));

    if (stats) {
      return (
        <ContactsImportStats {...stats} />
      );
    }

    return null;
  };

  loading = () => {
    const {
      isCreatingCampaign,
      isFetchingGroups,
      isUpdatingCampaign,
      isUploadingFile,
    } = this.props;
    const { scanning } = this.state;

    return isCreatingCampaign
      || isFetchingGroups
      || isUpdatingCampaign
      || isUploadingFile
      || scanning;
  };

  openSuccessModal = () => {
    const { file, fileUploadSuccess, success } = this.props;
    const { scanning, scanStatus, scanError, scanSucceeded } = this.state;

    if (scanning || isScanPending(scanStatus) || scanError) {
      return false;
    }

    const opOk = success || scanSucceeded;
    return opOk && (!file || fileUploadSuccess || scanSucceeded);
  };

  handleScanResponse = async (id, uploadResponse) => {
    const payload = (uploadResponse && uploadResponse.data) || uploadResponse || {};
    const status = getScanStatus(payload);
    const syncStats = extractImportStats(payload);

    if (syncStats && !isScanPending(status)) {
      this.setState({
        importStats: syncStats,
        scanStatus: status || 'done',
        scanning: false,
        scanError: null,
        scanSucceeded: true,
      });
      return;
    }

    if (!isScanPending(status)) {
      return;
    }

    this.setState({
      scanning: true,
      scanStatus: status,
      scanError: null,
      scanSucceeded: false,
    });
    try {
      const fetchFn = this.props.fetchCampaign || this.props.getCampaign;
      const campaign = await pollCampaignScan(fetchFn, id, {
        onProgress: (nextStatus) => {
          this.setState({ scanStatus: nextStatus });
        },
      });
      if (campaign.scan_status === 'failed') {
        this.setState({
          scanning: false,
          scanStatus: 'failed',
          scanError: campaign.scan_error || 'Error al procesar el archivo',
          scanSucceeded: false,
        });
        return;
      }
      this.setState({
        scanning: false,
        scanStatus: 'done',
        importStats: extractImportStats({ data: campaign }),
        scanError: null,
        scanSucceeded: true,
      });
    } catch (e) {
      this.setState({
        scanning: false,
        scanStatus: 'failed',
        scanError: e.message || 'Error al procesar el archivo',
        scanSucceeded: false,
      });
    }
  };

  parseGroups = () => (
    this.props.groups.map(({ id: value, name: text }) => ({
      text, value,
    }))
  );

  uploadContactsFile = (id, contacts_src_file) => (
    this.props.uploadFile(
      `${CAMPAIGNS_URL}/${id}`,
      {
        contacts_src_file,
        contacts_src_file_name: contacts_src_file.name,
      },
      {
        headers,
      },
    ).then(res => this.handleScanResponse(id, res))
  );

  componentDidMount() {
    const { groupId, groupsLoaded } = this.props;

    this.props.uploadFileReset();

    if (!groupsLoaded) {
      this.props.fetchAllGroups({}, { query: { client: true } });
    }

    if (groupId) {
      this.props.change(form, 'group_id', groupId);
    }
  }

  render() {
    const { history, onAcceptClick } = this.props;
    const groups = this.parseGroups();
    const importStats = this.getImportStats();
    const subject = trans('resources.singular.campaign');

    return (
      <ModalsWrappedForm
        acceptText={
          onAcceptClick
            ? trans('modals.actions.accept')
            : trans('modals.actions.backToList')
        }
        extraInfo={importStats}
        gender={FEMALE_CODE}
        history={history}
        loading={this.loading()}
        onAcceptClick={onAcceptClick && (() => onAcceptClick(true))}
        open={this.openSuccessModal()}
        path={PRIVATE_PATHS.clientCampaigns.path}
        subject={subject}
        successMessage="successOpSingF"
        type="add"
      >
        <AddCampaignForm
          actions={this.getActions()}
          data={{
            group_id: groups,
          }}
          history={history}
          onSubmit={this.createCampaign}
          path={PRIVATE_PATHS.clientCampaigns.path}
        />
      </ModalsWrappedForm>
    );
  }
}

const selector = formValueSelector(form);

const mapStateToProps = ({
  allGroups: {
    isFetching: isFetchingGroups,
    items: groups,
  },
  campaigns: { isCreating, isUpdating },
  fileUpload: { pending, response, success: fileUploadSuccess },
  reduxRestResponse: { body, success, type },
  ...state
}) => ({
  body,
  isFetchingGroups,
  response,
  file: selector(state, 'file'),
  fileUploadSuccess: !selector(state, 'contacts_src_file') || fileUploadSuccess,
  groups: groups[1] ? groups[1] : [],
  isCreatingCampaign: isCreating,
  isUpdatingCampaign: isUpdating,
  isUploadingFile: pending,
  success: (
    type === types.CREATE_CAMPAIGN || type === types.UPDATE_CAMPAIGN
  ) && success,
});

export default connect(
  mapStateToProps,
  {
    ...allGroupsActions,
    ...campaignsActions,
    ...fileUploadActions,
    change,
  }
)(AddCampaign);
